<template>
  <div :class="filtered ? 'container-fluid product-wrapper sidebaron' : 'container-fluid product-wrapper'">
    <div class="product-grid">
      
      <div class="feature-products">
        <BaseFilter
          screenKey="balance-histories-list-page"
          :model-value="allParams"
          @update:model-value="val => Object.assign(allParams, val)"
          :fields="filterConfig"
          @search="handleSearch"
          @reset="handleReset"
        />
      </div>
      
      <div class="p-4 bg-white mt-3 shadow-sm rounded">
        <div class="d-flex justify-content-between align-items-center mb-4">
          <h3 class="m-0">Balance Histories List</h3>
          <div class="table-tools">
             <button class="btn btn-sm btn-outline-secondary" @click="fetchData">
               <i class="fa fa-refresh me-1"></i> Refresh
             </button>
          </div>
        </div>

        <BaseTable
          storage-key="balance-histories"
          :columns="tableCols"
          :data="tableData"
          :loading="loading"
          :total-items="total"
          v-model:current-page="allParams.page"
          v-model:page-size="allParams.limit"
          show-checkbox
          @sort="handleSort"
          @selection-change="handleSelection"
        >
          <template #cell(user_id)="{ item }">
            {{ item.user?.name || item.user_id }}
          </template>
          <template #cell(order_id)="{ item }">
            {{ item.order?.code || item.order_id }}
          </template>
          <template #cell(payment_id)="{ item }">
            {{ item.payment?.code || item.payment_id }}
          </template>
          <template #cell(service_id)="{ item }">
            {{ getServiceName(item.order?.service_id) }}
          </template>
          <template #cell(amount)="{ item }">
            <span :class="Number(item.amount) >= 0 ? 'text-success fw-bold' : 'text-danger fw-bold'">
              {{ Number(item.amount).toLocaleString() }}
            </span>
          </template>
          <template #cell(old_balance)="{ item }">
            {{ Number(item.old_balance).toLocaleString() }}
          </template>
          <template #cell(new_balance)="{ item }">
            {{ Number(item.new_balance).toLocaleString() }}
          </template>
          <template #cell(description)="{ item }">
             <span v-if="item.description" class="d-inline-block text-truncate" style="max-width: 50px;" :title="item.description">
              {{ item.description }}
            </span>
          </template>
          <template #cell(created_at)="{ item }">
            {{ formatDateTime(item.created_at) }}
          </template>
        </BaseTable>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, reactive, onMounted, watch } from 'vue';

import BaseFilter from "@/components/common/BaseFilter.vue";
import BaseTable from "@/components/common/BaseTable.vue";
import { useBalanceHistories } from "@/composables/useBalanceHistories";
import { useService } from "@/composables/useService";
import { createToaster } from "@meforma/vue-toaster";

const toaster = createToaster({
  position: "top-right",
  duration: 2000,
});
const { fetchBalanceHistoriesApi } = useBalanceHistories();
const filtered = ref(false);
const loading = ref(false);
const tableData = ref([]);
const total = ref(0);

const allParams = reactive({
  page: 1,
  limit: 10,
  orderBy: 'id',
  sortedBy: 'desc',
  order: '',
  searchJoin: 'AND',
  include: 'user,order,payment',
  searchFields: '',
  search: '',
});

// Cấu hình các ô nhập của Filter
const filterConfig = [
  {
    key: 'order_id',
    type: 'text',
    operator: '=',
    label: 'Order ID',
    placeholder: 'Order ID...',
    isDefault: true
  },
  {
    key: 'user_id',
    type: 'text',
    operator: '=',
    label: 'User ID',
    placeholder: 'User ID...',
    isDefault: true
  }
];

// Cấu hình các cột của Bảng
const tableCols = [
  { key: 'id', label: 'ID', width: '70px', sortable: true },
  { key: 'user_id', label: 'User' },
  { key: 'order_id', label: 'Order' },
  { key: 'payment_id', label: 'Payment' },
  { key: 'service_id', label: 'Service' },
  { key: 'amount', label: 'Amount', align: 'center' },
  { key: 'old_balance', label: 'Old Balance', align: 'center' },
  { key: 'new_balance', label: 'New Balance', align: 'center' },
  { key: 'description', label: 'Description' },
  { key: 'created_at', label: 'Created At' },
];

const fetchData = async (resetPage = false) => {
  if (resetPage) {
    allParams.page = 1;
  }

  loading.value = true;

  try {
    const res = await fetchBalanceHistoriesApi({ ...allParams });

    if (res.success && res.data) {
      tableData.value = res.data.data || [];

      total.value = res.data.meta?.pagination?.total || 0;
    } else {
      toaster.error(res.error || "Không thể tải dữ liệu");
    }
  } catch (err) {
    toaster.error("Có lỗi xảy ra trong quá trình kết nối");
  } finally {
    loading.value = false;
  }
};

const handleSearch = () => {
  if (allParams.page === 1) fetchData();
  else allParams.page = 1;
};

const handleReset = () => {
  allParams.search = '';
  allParams.searchFields = '';
  
  if (allParams.page === 1) fetchData();
  else allParams.page = 1;
};

const handleSort = ({ key, order }) => {
  allParams.orderBy = key;
  allParams.order = order;
  fetchData();
};

const handleSelection = (ids) => {
  console.log("IDs đã chọn:", ids);
};

watch(() => [allParams.page, allParams.limit], () => fetchData());

const services = ref([]);
const { getServicesApi } = useService();

const fetchDependencies = async () => {
  try {
    const serviceRes = await getServicesApi({ limit: 1000 });
    if (serviceRes.success && serviceRes.data) {
      services.value = serviceRes.data.data || [];
    }
  } catch (e) {
    console.error(e);
  }
};

const getServiceName = (id) => {
  if (!id) return '';
  const service = services.value.find(s => s.id === Number(id));
  return service ? service.name : id;
};

onMounted(() => {
  fetchDependencies();
  fetchData();
});

const formatDateTime = (dateStr) => {
  if (!dateStr) return '';
  const date = new Date(dateStr);

  const d = String(date.getDate()).padStart(2, '0');
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const y = date.getFullYear();

  const h = String(date.getHours()).padStart(2, '0');
  const i = String(date.getMinutes()).padStart(2, '0');
  const s = String(date.getSeconds()).padStart(2, '0');

  return `${d}/${m}/${y} ${h}:${i}:${s}`;
};
</script>

<style scoped>
.filter-section .left-filter {
  max-height: 400px;
  overflow-y: auto;
  z-index: 99 !important;
}
.product-wrapper {
  padding-bottom: 50px;
}
</style>